import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import logo from "../../images/logo.png";

const SignUp = () => {
    const navigate = useNavigate();
    // Set by acknowledgement.jsx when a patient completes the questionnaire before
    // creating an account — lets the backend link that assessment to this account.
    const location = useLocation();
    const assessmentId = location.state?.assessmentId ?? null;
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }));
        }
    };

    const validate = () => {
        const newErrors = {};

        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
        } else if (formData.name.trim().length < 2) {
            newErrors.name = "Name must be at least 2 characters";
        }

        // Email validation
        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address";
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = "Password is required";
        } else if (formData.password.length < 6) {
            newErrors.password = "Password must be at least 6 characters";
        } else if (!/(?=.*[a-z])(?=.*[A-Z])/.test(formData.password)) {
            newErrors.password = "Password must contain at least one uppercase and one lowercase letter";
        }

        // Confirm Password validation
        if (!formData.confirmPassword) {
            newErrors.confirmPassword = "Please confirm your password";
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSubmitting(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/register`, {
                name: formData.name,
                email: formData.email,
                password: formData.password,
                password_confirmation: formData.confirmPassword,
                ...(assessmentId ? { assessment_id: assessmentId } : {}),
            });

            toast.success("Account created successfully. Please login.");
            setIsSubmitting(false);
            setTimeout(() => navigate("/auth/login"), 900);
        } catch (err) {
            const apiMsg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Signup failed. Please try again.";
            const fieldErrors = err?.response?.data?.errors;
            setErrors((prev) => ({
                ...prev,
                api: apiMsg,
                ...(fieldErrors || {}),
            }));
            toast.error(apiMsg);
            setIsSubmitting(false);
        }
    };

    const isFormFilled =
        formData.name.trim() &&
        formData.email.trim() &&
        formData.password &&
        formData.confirmPassword;

    return (
        <>
            {isSubmitting && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                    <div className="h-14 w-14 rounded-full border-4 border-[#56C7D5] border-t-transparent animate-spin" />
                </div>
            )}
            <section className="bg-[#F5F7FB] min-h-screen flex items-center justify-center font-poppins py-10">
                <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-sm w-full max-w-xl px-6 sm:px-8 py-8 sm:py-10">
                    {/* Logo */}
                    <div className="flex justify-center mb-6">
                        <Link to="/">
                            <img
                                src={logo}
                                alt="Slimming Doctor"
                                className="h-12 sm:h-14 object-contain"
                            />
                        </Link>
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl sm:text-3xl font-bold text-[#2A3342] text-center mb-2">
                        Sign Up
                    </h2>
                    <p className="text-center text-gray-600 mb-8">
                        Create your account to get started.
                    </p>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {errors.api && <p className="text-center text-red-500 text-sm">{errors.api}</p>}
                        {/* Name */}
                        <div>
                            <label className="block text-[16px] font-medium text-black mb-1">
                                Full Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="name"
                                placeholder="Enter your full name"
                                value={formData.name}
                                onChange={handleChange}
                                className={`w-full border rounded-md px-3 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#35C3D9] focus:border-[#35C3D9] ${errors.name ? "border-red-500" : "border-gray-200"
                                    }`}
                            />
                            {errors.name && (
                                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                            )}
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-[16px] font-medium text-black mb-1">
                                Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                placeholder="Enter your email address"
                                value={formData.email}
                                onChange={handleChange}
                                className={`w-full border rounded-md px-3 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#35C3D9] focus:border-[#35C3D9] ${errors.email ? "border-red-500" : "border-gray-200"
                                    }`}
                            />
                            {errors.email && (
                                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                            )}
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-[16px] font-medium text-black mb-1">
                                Password <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="password"
                                name="password"
                                placeholder="Enter your password"
                                value={formData.password}
                                onChange={handleChange}
                                className={`w-full border rounded-md px-3 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#35C3D9] focus:border-[#35C3D9] ${errors.password ? "border-red-500" : "border-gray-200"
                                    }`}
                            />
                            {errors.password && (
                                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label className="block text-[16px] font-medium text-black mb-1">
                                Confirm Password <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="password"
                                name="confirmPassword"
                                placeholder="Confirm your password"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className={`w-full border rounded-md px-3 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#35C3D9] focus:border-[#35C3D9] ${errors.confirmPassword ? "border-red-500" : "border-gray-200"
                                    }`}
                            />
                            {errors.confirmPassword && (
                                <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isSubmitting || !isFormFilled}
                            className={`w-full bg-[#56C7D5] hover:bg-[#37bfd9] text-white font-[500] px-10 py-3 rounded-lg transition ${isSubmitting || !isFormFilled ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                        >
                            {isSubmitting ? "Creating Account..." : "Sign Up"}
                        </button>

                        {/* Login Link */}
                        <p className="text-center text-sm text-gray-600">
                            Already have an account?{" "}
                            <Link
                                to="/auth/login"
                                className="text-[#56C7D5] hover:text-[#37bfd9] font-medium"
                            >
                                Login
                            </Link>
                        </p>
                    </form>
                </div>
                <ToastContainer position="top-right" autoClose={2500} hideProgressBar />
            </section>
        </>
    );
};

export default SignUp;